import { fetchApi } from '@/shared/api/fetchApi';
import { CarModel as CarModelFromApi } from '@/features/car-models/api/carModelsApi';

export interface CarEnums {
  drivetrain: Array<{ label: string; value: string; }>;
  fuel_type: Array<{ label: string; value: string; }>;
}

export interface CarTechProfile {
  // добавить поля после уточнения структуры
}

// Используем тип из car-models API
export type CarModel = CarModelFromApi;

export interface Car {
  id: string;
  slug: string;
  color: string;
  model: string;  // ID модели
  release_year: number;
  registration_number: string;
  description_en: string;
  description_fr: string;
  price: string;
  image: string;
  image_url: string;
  ecology_class: string;
  fuel_type: string;
  mileage_limit: number;
  drivetrain: string;
  fuel_consumption: string;
  engine_displacement: string;
  engine_power: string;
  seats_count: number;
  doors_count: number;
  has_gps: boolean;
  clearance: number;
  permitted_area_latitude: string;
  permitted_area_longitude: string;
  permitted_area_radius: number;
}

// Тип для машины с загруженной моделью
export interface CarWithLoadedModel extends Omit<Car, 'model'> {
  model: CarModel;
  busy_days: number;
  free_days: number;
  nearest_rent?: {
    id: string;
    status: string;
    start_date: string;
    end_date: string;
    start_time: string;
    end_time: string;
    address_to_drop: string;
    address_to_deliver: string;
  };
}

export interface CreateCarDto {
  model: string;      // UUID модели
  slug: string;       // уникальный идентификатор
  color: string;
  registration_number: string;
  release_year: number;
  description_en: string;
  description_fr: string;
  price: string;
  ecology_class: string;
  fuel_type: 'petrol' | 'diesel' | 'electric';
  mileage_limit: number;
  drivetrain: 'front_2wd' | 'rear_2wd' | 'full_4wd';
  fuel_consumption: number;
  engine_displacement: number;
  engine_power: number;
  seats_count: number;
  doors_count: number;
  has_gps: boolean;
  clearance: number;
  permitted_area_latitude: number;
  permitted_area_longitude: number;
  permitted_area_radius: number;
  image?: File;
}

// Тип для ответа API
export interface CarsApiResponse {
  data: Car[];
  total: number;
  offset: number;
  limit: number;
}

export interface CarsParams {
  offset?: number;
  limit?: number;
  fuel_type?: string;
  brand?: string;
  model?: string;
  with_stats?: boolean;  // Добавляем параметр для запроса статистики
}

async function uploadImage(id: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);
  return fetchApi(`/api/v1/cars/${id}/image/upload`, {
    method: 'POST',
    body: formData,
  });
}

export const carsApi = {
  getEnums: async (): Promise<CarEnums> => {
    return Promise.resolve({
      drivetrain: [
        { label: "Front 2Wd", value: "front_2wd" },
        { label: "Rear 2Wd", value: "rear_2wd" },
        { label: "Full 4Wd", value: "full_4wd" }
      ],
      fuel_type: [
        { label: "Petrol", value: "petrol" },
        { label: "Diesel", value: "diesel" },
        { label: "Electric", value: "electric" }
      ]
    });
  },

  getCars: async (params: CarsParams = {}): Promise<CarsApiResponse> => {
    const searchParams = new URLSearchParams();
    if (params.offset !== undefined) searchParams.append('offset', params.offset.toString());
    if (params.limit !== undefined) searchParams.append('limit', params.limit.toString());
    if (params.fuel_type) searchParams.append('fuel_type', params.fuel_type);
    if (params.brand) searchParams.append('brand', params.brand);
    if (params.model) searchParams.append('model', params.model);

    const url = `/api/v1/cars/list?${searchParams.toString()}`;
    return fetchApi<CarsApiResponse>(url);
  },

  getCarById: async (id: string): Promise<Car> => {
    return fetchApi<Car>(`/api/v1/cars/${id}`);
  },

  createCar: async (data: CreateCarDto): Promise<Car> => {
    const { image, ...carData } = data;
    const car = await fetchApi<Car>('/api/v1/cars/create', {
      method: 'POST',
      body: JSON.stringify(carData),
    });

    if (image) {
      await uploadImage(car.id, image);
    }

    return car;
  },

  updateCar: async (id: string, data: Partial<Car>): Promise<Car> => {
    return fetchApi<Car>(`/api/v1/cars/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  getTechProfile: async (id: string): Promise<CarTechProfile> => {
    return fetchApi<CarTechProfile>(`/api/v1/cars/${id}/tech_profile`);
  },

  uploadCarImage: async (id: string, file: File): Promise<Car> => {
    const formData = new FormData();
    formData.append('file', file);

    return fetchApi<Car>(`/api/v1/cars/${id}/image/upload`, {
      method: 'PATCH',
      body: formData,
    });
  },

  deleteCar: async (id: string): Promise<void> => {
    await fetchApi(`/api/v1/cars/${id}`, {
      method: 'DELETE',
    });
  },
};
